import { AxiosError } from 'axios';
import { UseQueryResult, useMutation, useQuery, useQueryClient } from 'react-query';
import { api, TMSResponse } from './service-api';
import { httpClient } from 'redux/reducers/settings';

export interface PaginatedResponse<T = any> {
  count: number;
  next: number | null;
  previous: number | null;
  results: T;
}

export interface IProvince {
  created_at: string;
  id: number;
  name_en: string;
  name_np: string;
  province_code: string;
  province_number: number;
  updated_at: string;
}

export interface IDistricts {
  id: number;
  created_at: string;
  updated_at: string;
  name_en: string;
  name_np: string;
  province: number;
}

export interface DistrictPayload {
  total_rows: number;
  districts: IDistricts[];
}

export interface DistrictResponse {
  code: number;
  message: string;
  data: DistrictPayload;
}

export interface IEthnics {
  id: number;
  name: string;
}
export interface EthnicityPayload {
  total_rows: number;
  ethnicities: IEthnics[];
}

export interface EthnicityResponse {
  code: number;
  message: string;
  data: EthnicityPayload;
}

export interface IServiceGroup {
  id: number;
  name: string;
}

const fetchEthnics = () => {
  return httpClient.get<EthnicityResponse>(api.location.ethnicity);
};

const fetchServiceGroup = () => {
  return httpClient.get<IServiceGroup[]>(api.location.serviceGroup);
};
const useEthnicityQuery = () => {
  return useQuery(api.location.ethnicity, fetchEthnics, {
    select: (response) => response.data.data.ethnicities
  });
};

const useServiceGroupQuery = () => {
  return useQuery(api.location.serviceGroup, fetchServiceGroup, {
    select: ({ data }: any) => {
      return data?.data;
    }
  });
};

const fetchProvinces = () => {
  return httpClient.get<PaginatedResponse<IProvince[]>>(api.location.fetchProvinces);
};

const useProvincesQuery = () => {
  return useQuery(api.location.fetchProvinces, fetchProvinces, {
    select: ({ data }: any) => {
      return data;
    }
  });
};

const fetchDistricts = () => () => {
  return httpClient.get<DistrictResponse>(api.location.fetchDistricts, {
    params: {
      page: 1,
      page_size: 1000
    }
  });
};

const useDistrictsQuery = (
  onSuccess?: (data: any) => void
) => {
  return useQuery([api.location.fetchDistricts], fetchDistricts(), {
    cacheTime: 0,
    staleTime: 0,
    select: (response) => response.data.data.districts,
    onSuccess
  });
};

const fetchServiceSubGroup = (serviceId?: string) => () => {
  return httpClient.get<any>(api.location.serviceSubGroup.replace('{serviceId}', serviceId ?? ''));
};

const useServiceSubGroupQuery = (serviceId?: string, onSuccess?: (data: any) => void) => {
  return useQuery([api.location.serviceSubGroup, serviceId], fetchServiceSubGroup(serviceId), {
    cacheTime: 0,
    enabled: !!serviceId,
    select: ({ data }) => {
      return data;
    },
    onSuccess
  });
};

const getBlobFromUrl = async (url: string) => {
  const response = await httpClient.get<Blob>(url, {
    responseType: 'blob'
  });
  return response;
};

const useFileFromUrl = (url?: string): UseQueryResult<File, unknown> => {
  const filename = url?.split('/').pop() || '';
  const extension = url?.split('.').pop() || '';
  return useQuery(['blob', url], () => getBlobFromUrl(url ?? ''), {
    select: (response) =>
      new File([response.data], filename, {
        type: 'image/jpeg'
      }),
    enabled: !!url
  });
};

export {
  useProvincesQuery,
  useDistrictsQuery,
  useEthnicityQuery,
  useServiceGroupQuery,
  useServiceSubGroupQuery,
  useFileFromUrl
};
